import { AlertCircle } from '../../ui/icons';
import { OpsGameDataLogEntry } from '../../../services/api';
import { useMqttFeed, MqttStatusBar } from './MqttFeed';

interface LiveGameDataMqttProps {
  gameName: string;
  /** Called for each incoming message, normalized to the same shape as the historical
   * DynamoDB-sourced game-data entries, so the caller can merge live + historical into one list. */
  onMessage: (entry: OpsGameDataLogEntry) => void;
}

/** Live feed of a game's raw score/clock records over MQTT - unlike the radio-tipoff feed, the
 * topic is keyed directly by game_name, no game/task-ARN lookup needed. */
export default function LiveGameDataMqtt({ gameName, onMessage }: LiveGameDataMqttProps) {
  const topic = gameName ? `homestream/games/${gameName}` : null;

  const { status, error, connect, disconnect } = useMqttFeed(topic, (raw) => {
    let parsed: Record<string, unknown> = {};
    try {
      const json = JSON.parse(raw);
      if (json && typeof json === 'object') parsed = json as Record<string, unknown>;
    } catch {
      return; // Not JSON - nothing usable to show as a game-data row.
    }
    // Same field-name fallbacks as the backend's get_game_data_logs (quarter/period,
    // play_clock/shot_clock), since this is the same underlying data feed.
    const entry: OpsGameDataLogEntry = {
      source_utc: String(parsed.source_utc ?? ''),
      home_score: Number(parsed.home_score ?? 0),
      guest_score: Number(parsed.guest_score ?? 0),
      clock: String(parsed.clock ?? ''),
      period: (parsed.quarter ?? parsed.period ?? '') as string | number,
      period_text: String(parsed.period_text ?? ''),
      clock_status: String(parsed.clock_status ?? ''),
      play_clock: String(parsed.play_clock ?? parsed.shot_clock ?? ''),
      venue_name: String(parsed.venue_name ?? ''),
    };
    onMessage(entry);
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <MqttStatusBar status={status} topicLabel={topic} onConnect={connect} onDisconnect={disconnect} />

      {error && (
        <div className="alert alert-warning" style={{ margin: 0 }}>
          <AlertCircle size={18} />
          <div>{error}</div>
        </div>
      )}
    </div>
  );
}
